import {ReactNode, useRef, useState} from "react";
import "./BracketCanvas.scss";

interface BracketCanvasProps {
    /** Red eyebrow above the title (e.g. "Live Bracket"). */
    eyebrow?: string;
    /** Header title — the "." accent is appended automatically. */
    title?: string;
    children: ReactNode;
}

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 1.4;
const ZOOM_STEP = 0.1;

/**
 * Viewport wrapper for wide brackets: a header row (eyebrow + title on the left,
 * zoom on the right) over a drag-to-pan, zoomable column area. Wraps the round
 * columns so single/double-elim brackets that overflow the page stay navigable.
 */
export default function BracketCanvas({eyebrow, title, children}: BracketCanvasProps) {
    const viewportRef = useRef<HTMLDivElement>(null);
    const [zoom, setZoom] = useState(1);
    const drag = useRef<{ startX: number; startScroll: number } | null>(null);
    const [dragging, setDragging] = useState(false);

    const onMouseDown = (e: React.MouseEvent) => {
        const vp = viewportRef.current;
        if (!vp) return;
        drag.current = {startX: e.pageX, startScroll: vp.scrollLeft};
        setDragging(true);
    };

    const onMouseMove = (e: React.MouseEvent) => {
        const vp = viewportRef.current;
        if (!vp || !drag.current) return;
        vp.scrollLeft = drag.current.startScroll - (e.pageX - drag.current.startX);
    };

    const endDrag = () => {
        drag.current = null;
        setDragging(false);
    };

    const zoomBy = (delta: number) =>
        setZoom((z) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round((z + delta) * 10) / 10)));

    /*
     * Headingless = embedded context (e.g. the Drop-In tournament panel's
     * mini-bracket) — no toolbar chrome at all, per the mockups.
     */
    const showToolbar = Boolean(eyebrow || title);

    return (
        <div className="bracket-canvas">
            {showToolbar &&
            <div className="bracket-canvas__toolbar">
                <div className="bracket-canvas__heading">
                    {eyebrow && <div className="bracket-canvas__eyebrow">{eyebrow}</div>}
                    {title &&
                        <h2 className="bracket-canvas__title">
                            {title}<span className="bracket-canvas__title-dot">.</span>
                        </h2>
                    }
                </div>
                <div className="bracket-canvas__zoom">
                    <button
                        type="button"
                        className="bracket-canvas__zoom-btn"
                        onClick={() => zoomBy(-ZOOM_STEP)}
                        disabled={zoom <= ZOOM_MIN}
                        aria-label="Zoom out"
                    >
                        −
                    </button>
                    <button
                        type="button"
                        className="bracket-canvas__zoom-level"
                        onClick={() => setZoom(1)}
                        aria-label="Reset zoom"
                    >
                        {Math.round(zoom * 100)}%
                    </button>
                    <button
                        type="button"
                        className="bracket-canvas__zoom-btn"
                        onClick={() => zoomBy(ZOOM_STEP)}
                        disabled={zoom >= ZOOM_MAX}
                        aria-label="Zoom in"
                    >
                        +
                    </button>
                </div>
            </div>
            }

            <div
                ref={viewportRef}
                className={`bracket-canvas__viewport${dragging ? " bracket-canvas__viewport--dragging" : ""}`}
                onMouseDown={onMouseDown}
                onMouseMove={onMouseMove}
                onMouseUp={endDrag}
                onMouseLeave={endDrag}
            >
                <div
                    className="bracket-canvas__content"
                    style={{transform: `scale(${zoom})`, transformOrigin: "top left"}}
                >
                    {children}
                </div>
            </div>
        </div>
    );
}
